import type { ListPageResult, ListRow } from '@/components/astur/list-page';
import type { Integration, IntegrationSubscription } from '@/types/integration';
import type { OrderIssueRelevantIdType, SubscriptionNotification, SubscriptionNotificationsSearchResponse } from '@/types/subscriptionNotification';
import { isRecord } from '@/utils/dataUtils';

/** The scope for seeing order issues anywhere. The search endpoint has no scope check of its own. */
export const ORDER_ISSUES_SCOPE = 'order:read';

const EM_DASH = '—';

/** Relevant-id types meant to hold an Acenda order id. */
const ACENDA_RELEVANT_ID_TYPES = ['order', 'order_id'] as const satisfies readonly OrderIssueRelevantIdType[];

type AcendaRelevantIdType = (typeof ACENDA_RELEVANT_ID_TYPES)[number];

/** Relevant-id types that hold a sales channel or provider order reference instead, with the label shown before it. */
const EXTERNAL_ORDER_ID_LABELS: Readonly<Record<Exclude<OrderIssueRelevantIdType, AcendaRelevantIdType>, string>> = {
  external_order: 'External',
  external_order_id: 'External',
  channel_order_id: 'Channel Order',
  purchase_order: 'PO',
};

const EXTERNAL_RELEVANT_ID_TYPES = [
  'external_order',
  'external_order_id',
  'channel_order_id',
  'purchase_order',
] as const satisfies readonly (keyof typeof EXTERNAL_ORDER_ID_LABELS)[];

const isAcendaRelevantIdType = (type: OrderIssueRelevantIdType): type is AcendaRelevantIdType =>
  (ACENDA_RELEVANT_ID_TYPES as readonly OrderIssueRelevantIdType[]).includes(type);

const ACENDA_ORDER_ID = /^[1-9]\d*$/;

/** Filed before an order exists in Acenda, so its id is never an Acenda order id, whatever its relevant-id type says. */
const ORDER_PULL = 'order_pull';

/** Marketplaces file failed imports under this type, with their own order id. */
const ORDER_INSERT = 'order_insert';

/** The one channel whose `order_insert` rows are about an existing Acenda order: it pushes that order to the provider. */
const SELLERCLOUD = 'sellercloud';

type OrderLinkFields = Readonly<Pick<SubscriptionNotification, 'relevant_id' | 'relevant_id_type' | 'type' | 'channel_name'>>;

/**
 * Whether the issue's relevant_id really is an Acenda order id, so it can link to the order. Some rows filed under
 * `order` hold a marketplace id or nothing, so the relevant-id type alone can't say.
 */
export const isAcendaOrderIssue = (row: OrderLinkFields): boolean => {
  if (!isAcendaRelevantIdType(row.relevant_id_type) || !ACENDA_ORDER_ID.test(row.relevant_id)) return false;
  if (row.type === ORDER_PULL) return false;
  return row.type !== ORDER_INSERT || row.channel_name.toLowerCase() === SELLERCLOUD;
};

/** The issue's order id as shown: an Acenda order id as is, a blank id as an em dash, anything else with its label (`External: 12345`). */
export const orderIssueIdLabel = (row: OrderLinkFields): string => {
  if (row.relevant_id.trim() === '') return EM_DASH;
  if (isAcendaOrderIssue(row)) return row.relevant_id;
  const label = isAcendaRelevantIdType(row.relevant_id_type) ? 'External' : EXTERNAL_ORDER_ID_LABELS[row.relevant_id_type];
  return `${label}: ${row.relevant_id}`;
};

/** The notification types channel integrations file under an order's relevant id. */
type OrderIssueType =
  | 'fulfillment_push'
  | 'fulfillment_pull'
  | 'order_fulfillment_failed'
  | 'order_push'
  | 'order_pull'
  | 'order_insert'
  | 'cancel_push'
  | 'cancel_pull'
  | 'ship_advice'
  | 'feeds'
  | 'file'
  | 'twig_render'
  | 'subscription'
  | 'other';

const ORDER_ISSUE_TYPE_LABELS = {
  fulfillment_push: 'Push Fulfillments',
  fulfillment_pull: 'Pull Fulfillments',
  order_fulfillment_failed: 'Failed Fulfillments',
  order_push: 'Push Orders',
  order_pull: 'Pull Orders',
  order_insert: 'Import Orders',
  cancel_push: 'Push Cancellations',
  cancel_pull: 'Pull Cancellations',
  ship_advice: 'Ship Advice',
  feeds: 'Feeds',
  file: 'Files',
  twig_render: 'Templates',
  subscription: 'Subscription',
  other: 'Other',
} as const satisfies Readonly<Record<OrderIssueType, string>>;

/** Notification types offered by the Type filter, with the label shown in the filter and the Type column. */
export const ORDER_ISSUE_TYPE_OPTIONS: readonly Readonly<{ value: string; name: string }>[] = Object.entries(ORDER_ISSUE_TYPE_LABELS).map(
  ([value, name]) => ({ value, name })
);

/** `apparel-magic` → `Apparel Magic`, `shipstation-v2` → `Shipstation V2`, `order_routing` → `Order Routing`. */
const titleCaseSlug = (slug: string): string =>
  slug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => (/^v\d+$/i.test(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');

/** A notification type as shown: its Type filter label, else the type in title case. */
export const orderIssueTypeLabel = (type: string): string =>
  ORDER_ISSUE_TYPE_OPTIONS.find((option) => option.value === type)?.name ?? titleCaseSlug(type);

type OpenOrderIssuesQuery = Readonly<{
  organization_name: string;
  is_fixed: 0;
  relevant_id_type: Readonly<{ $in: readonly OrderIssueRelevantIdType[] }>;
  id: Readonly<{ $orderby: 'desc' }>;
}>;

/**
 * Every open order issue of the organization, newest first, including those filed under an external order reference.
 * With `types`, only those notification types. The endpoint does not scope by the organization header, so
 * `organization_name` must always be sent.
 */
export const organizationOrderIssuesQuery = (
  organization: string,
  types: readonly string[] = []
): OpenOrderIssuesQuery & Readonly<{ type?: Readonly<{ $in: readonly string[] }> }> => ({
  organization_name: organization,
  is_fixed: 0,
  relevant_id_type: { $in: [...ACENDA_RELEVANT_ID_TYPES, ...EXTERNAL_RELEVANT_ID_TYPES] },
  ...(types.length > 0 ? { type: { $in: types } } : {}),
  id: { $orderby: 'desc' },
});

/**
 * The open issues that can belong to an Acenda order, newest first: the organization's or, with `orderId`, one order's.
 * Failed pulls and imports are left out, so a marketplace id that matches an Acenda id can't attach to the wrong order.
 */
export const orderLinkedIssuesQuery = (
  organization: string,
  orderId?: string
): OpenOrderIssuesQuery &
  Readonly<{
    relevant_id?: string;
    type: Readonly<{ $nin: readonly string[] }>;
    $or: readonly [Readonly<{ type: Readonly<{ $nin: readonly string[] }> }>, Readonly<{ channel_name: string }>];
  }> => ({
  organization_name: organization,
  is_fixed: 0,
  relevant_id_type: { $in: ACENDA_RELEVANT_ID_TYPES },
  ...(orderId ? { relevant_id: orderId } : {}),
  type: { $nin: [ORDER_PULL] },
  $or: [{ type: { $nin: [ORDER_INSERT] } }, { channel_name: SELLERCLOUD }],
  id: { $orderby: 'desc' },
});

/**
 * A notification's channel as clients know it. A slug can name several integrations (`amazon` is both Amazon Marketplace
 * and Amazon FBA), so the row's subscription decides: its integration's name, else its own name. Without a subscription
 * match, the catalog name only when the slug names one integration, else the slug in title case (`apparel-magic` →
 * `Apparel Magic`, `shipstation-v2` → `Shipstation V2`).
 */
export const channelDisplayName = (
  row: Readonly<Pick<SubscriptionNotification, 'channel_name' | 'subscription_id'>>,
  subscriptions: readonly Readonly<Pick<IntegrationSubscription, 'id' | 'integration_id' | 'name'>>[],
  integrations: readonly Readonly<Pick<Integration, 'id' | 'name' | 'channel_name'>>[]
): string => {
  const subscription = subscriptions.find((candidate) => candidate.id === row.subscription_id);
  const integration = subscription && integrations.find((candidate) => candidate.id === subscription.integration_id);
  if (integration?.name) return integration.name;
  if (subscription?.name) return subscription.name;
  const slug = row.channel_name.trim();
  const namedBySlug = integrations.filter((candidate) => candidate.channel_name?.toLowerCase() === slug.toLowerCase());
  return namedBySlug.length === 1 && namedBySlug[0].name ? namedBySlug[0].name : titleCaseSlug(slug);
};

/** The search's per-request cap; an organization with more open order issues gets counts for the newest ones only. */
export const ORDER_ISSUE_COUNTS_LIMIT = 1000;

/** Columns the count reads need; channel_name and subscription_id keep rows in the endpoint's grouping. */
export const ORDER_ISSUE_COUNT_FIELDS = ['id', 'channel_name', 'subscription_id', 'relevant_id'] as const;

type OrderIssueCountRow = Pick<SubscriptionNotification, (typeof ORDER_ISSUE_COUNT_FIELDS)[number]>;

/** One search page, ungrouped: the endpoint groups each page by channel and subscription, or sends `[]` when empty. */
const toSearchPage = <Row extends ListRow>(payload: SubscriptionNotificationsSearchResponse<Row> | null): ListPageResult<Row> => {
  const data = payload?.data;
  const total = payload?.total;
  if (typeof total !== 'number' || !Number.isInteger(total) || total < 0) return { ok: false, error: 'the response had no total' };
  if (Array.isArray(data)) return { ok: true, rows: data, count: total };
  if (!isRecord(data)) return { ok: false, error: 'the response had no rows' };
  const rows = Object.values(data).flatMap((groups) =>
    isRecord(groups) ? Object.values(groups).flatMap((group) => (Array.isArray(group) ? group : [])) : []
  );
  return { ok: true, rows, count: total };
};

/** Open issues per order id, from one search page; an unusable response counts nothing. */
export const toOrderIssueCounts = (payload: SubscriptionNotificationsSearchResponse<OrderIssueCountRow> | null): ReadonlyMap<string, number> => {
  const page = toSearchPage(payload);
  const counts = new Map<string, number>();
  if (!page.ok) return counts;
  for (const row of page.rows) {
    const orderId = String(row.relevant_id ?? '').trim();
    if (orderId) counts.set(orderId, (counts.get(orderId) ?? 0) + 1);
  }
  return counts;
};

/** The open order issue total from a search response; anything without a valid paginator total counts as none. */
export const toOrderIssuesTotal = (payload: Readonly<Pick<SubscriptionNotificationsSearchResponse, 'total'>> | null): number => {
  const total = payload?.total;
  return typeof total === 'number' && Number.isInteger(total) && total > 0 ? total : 0;
};

/** One search page as ListPage rows. Grouping loses the server order, so the page is re-sorted newest first. */
export const toOrderIssuesPage = (payload: SubscriptionNotificationsSearchResponse | null): ListPageResult<SubscriptionNotification> => {
  const page = toSearchPage(payload);
  return page.ok ? { ...page, rows: [...page.rows].sort((a, b) => b.id - a.id) } : page;
};
